import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Skeleton, Stack } from "@mui/material";
import SectionWrapper from "../../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { ObservedBehaviorsFormValues } from "./interface";
import useGetObservedBehaviorsStrengths from "hooks/api/client/useGetObservedBehaviorsStrengths";
import useUpsertObservedBehaviorsStrengths from "hooks/api/client/useUpsertObservedBehaviorsStrengths";

const EMPTY_VALUES: ObservedBehaviorsFormValues = {
  directObservations: "",
  strengths: "",
  preferences: "",
};

export const ObservedBehaviorsAndStrengths: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { observedBehaviorsStrengths, refetchObservedBehaviorsStrengths, isFetched: isObservedBehaviorsStrengthsFetched } =
    useGetObservedBehaviorsStrengths(clientIdNum);

  const { upsertObservedBehaviorsStrengthsMutation, isUpsertObservedBehaviorsStrengthsLoading } =
    useUpsertObservedBehaviorsStrengths({
      onSuccess: () => {
        refetchObservedBehaviorsStrengths();
        setEditMode(false);
      },
    });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<ObservedBehaviorsFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<ObservedBehaviorsFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (observedBehaviorsStrengths) {
      const values: ObservedBehaviorsFormValues = {
        directObservations: observedBehaviorsStrengths.directObservations || "",
        strengths: observedBehaviorsStrengths.strengths || "",
        preferences: observedBehaviorsStrengths.preferences || "",
      };
      setFormValues(values);
      setInitialValues(values);
    }
  }, [observedBehaviorsStrengths]);

  const handleChange =
    (field: keyof ObservedBehaviorsFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    upsertObservedBehaviorsStrengthsMutation({ clientId: clientIdNum, payload: formValues });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isUpsertObservedBehaviorsStrengthsLoading;

  if (!isObservedBehaviorsStrengthsFetched) {
    return (
      <SectionWrapper
        title="Observed Behaviors & Strengths"
        description="Document direct observations, strengths, and preferences to build a personalized care plan."
        hideButtons
      >
        <Stack gap={2}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Observed Behaviors & Strengths"
      description="Document direct observations, strengths, and preferences to build a personalized care plan."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default ObservedBehaviorsAndStrengths;
